import React from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import annotationPlugin from 'chartjs-plugin-annotation'; // Import the annotation plugin

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  annotationPlugin // Register the annotation plugin
);

const NetWorthChart = ({ dailyNetWorthData, stockPriceData, transactionAnnotations }) => { // Add transactionAnnotations prop
  if (!dailyNetWorthData || dailyNetWorthData.length === 0) {
    return <p>沒有足夠的每日淨值資料可供繪製圖表。</p>;
  }

  // const labels = dailyNetWorthData.map(data => data.date); // Use net worth dates as primary labels

  const datasetsConfig = [
    {
      label: '每日資產淨值',
      data: dailyNetWorthData.map(data => ({ x: data.date, y: data.value })), // Ensure data is in {x, y} format for time scale
      fill: false,
      borderColor: 'rgb(75, 192, 192)',
      tension: 0.1,
      yAxisID: 'yNetWorth', // Assign to the net worth y-axis
      pointRadius: 0, // 移除資料點上的圓點
    },
  ];

  if (stockPriceData && stockPriceData.length > 0) {
    datasetsConfig.push({
      label: '股票收盤價',
      data: stockPriceData.map(data => ({ x: data.date, y: data.value })), // Ensure data is in {x, y} format for time scale
      borderColor: 'rgb(255, 99, 132)',
      tension: 0.1,
      yAxisID: 'yStockPrice', // Assign to the stock price y-axis
      fill: false,
      pointRadius: 0, // 移除資料點上的圓點
    });
  }

  const chartData = {
    // labels: labels, // Chart.js can infer labels from time series data if x values are provided
    datasets: datasetsConfig,
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: '每日資產淨值變化圖',
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
            label: function(context) {
                let label = context.dataset.label || '';
                if (label) {
                    label += ': ';
                }
                if (context.parsed.y !== null) {
                  if (context.dataset.yAxisID === 'yStockPrice') {
                    label += context.parsed.y.toFixed(2);
                  } else {
                    label += new Intl.NumberFormat('zh-TW').format(context.parsed.y);
                  }
                }
                return label;
            }
        }
      },
      annotation: { // Annotation plugin configuration
        annotations: transactionAnnotations ? transactionAnnotations.reduce((acc, ann) => {
          const netWorthPoint = dailyNetWorthData.find(d => d.date === ann.date);
          const uniqueSuffix = ann.label.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-]/g, ''); // Sanitize label for key
          
          if (netWorthPoint) { // netWorthPoint is still needed for the vertical line if we want it to align with a y-value, but for labels at top, it's less critical.
            // Text Label Annotation
            const labelConfig = {
              type: 'label',
              xValue: ann.date, // Keep x-position tied to the date
              yValue: Math.max(...dailyNetWorthData.map(d => d.value)) * 1.1, // 設定在最高值上方 2%
              yScaleID: 'yNetWorth', // 使用淨值軸的比例
              
              backgroundColor: 'rgba(0,0,0,0.05)',
              borderColor: 'rgba(0,0,0,0.3)',
              borderWidth: 1,
              borderRadius: 4,
              content: ann.label,
              font: {
                size: 9,
              },
              color: 'black',
              padding: 3,
            };

            labelConfig.xAdjust = 20;
            
            acc[`label-${ann.date}-${uniqueSuffix}`] = labelConfig;

            // Vertical Line Annotation
            acc[`line-${ann.date}-${uniqueSuffix}`] = {
              type: 'line',
              xMin: ann.date,
              xMax: ann.date,
              borderColor: 'rgba(0, 0, 0, 0.8)', // Adjusted line color to be less prominent
              borderWidth: 2, // Adjusted line width
              borderDash: [2, 2],
              // yMin and yMax are not set, so the line will span the entire chart height by default
            };
          }
          return acc;
        }, {}) : {}
      }
    },
    scales: {
      x: {
        type: 'time',
        time: {
          unit: 'day',
          tooltipFormat: 'yyyy-MM-dd',
          displayFormats: {
            day: 'yyyy-MM-dd'
          }
        },
        title: {
          display: true,
          text: '日期',
        },
      },
      yNetWorth: {
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: '資產淨值 (元)',
          color: 'rgb(75, 192, 192)',
        },
        ticks: {
          color: 'rgb(75, 192, 192)',
          callback: function(value) {
            return new Intl.NumberFormat('zh-TW').format(value);
          }
        }
      },
      yStockPrice: {
        type: 'linear',
        display: true,
        position: 'right',
        title: {
          display: true,
          text: '股票價格 (元)',
          color: 'rgb(255, 99, 132)',
        },
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          color: 'rgb(255, 99, 132)',
          callback: function(value) {
            return value.toFixed(2);
          }
        }
      },
    },
  };

  return (
    <div style={{ height: '400px', marginTop: '20px' }}>
      <Line data={chartData} options={options} />
    </div>
  );
};

export default NetWorthChart;